import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/hooks/use-toast';
import { showDomainErrorToast } from '@/lib/utils/domain-error-translator';

export interface DomainErrorLike {
  code: string;
}

export interface UseErrorToastHandlerDomainOptions {
  titleKey: string;
  defaultMessageKey: string;
  clearProgress?: () => void;
}

export interface UseErrorToastHandlerGenericOptions {
  titleKey: string;
  /** Description for the toast. If omitted, uses error when it's a string. */
  getDescription?: (error: unknown) => string;
}

export interface UseErrorToastHandlerParams {
  domainError: DomainErrorLike | null | undefined;
  isDomainError: boolean;
  error: unknown;
  domainToast?: UseErrorToastHandlerDomainOptions;
  genericToast?: UseErrorToastHandlerGenericOptions;
}

/**
 * Shows error toasts once per error instance (no duplicate on re-renders).
 * Use when you have reactive domain/generic error state from a hook.
 */
export function useErrorToastHandler({
  domainError,
  isDomainError,
  error,
  domainToast,
  genericToast,
}: UseErrorToastHandlerParams): void {
  const { t } = useTranslation();
  const { toast } = useToast();
  const shownDomainRef = useRef(false);
  const shownGenericRef = useRef(false);

  useEffect(() => {
    if (!domainToast) return;
    if (!isDomainError || !domainError) {
      if (!domainError) shownDomainRef.current = false;
      return;
    }
    if (shownDomainRef.current) return;
    shownDomainRef.current = true;
    showDomainErrorToast({
      toast,
      t,
      code: domainError.code,
      titleKey: domainToast.titleKey,
      defaultMessageKey: domainToast.defaultMessageKey,
      clearProgress: domainToast.clearProgress,
    });
  }, [domainError, isDomainError, domainToast, toast, t]);

  useEffect(() => {
    if (!genericToast) return;
    if (!error) {
      shownGenericRef.current = false;
      return;
    }
    if (isDomainError && domainError) return;
    if (shownGenericRef.current) return;
    shownGenericRef.current = true;
    domainToast?.clearProgress?.();
    const description =
      genericToast.getDescription?.(error) ??
      (typeof error === 'string' ? error : undefined);
    toast({
      title: t(genericToast.titleKey),
      ...(description !== undefined && { description }),
      variant: 'destructive',
    });
  }, [domainError, error, genericToast, isDomainError, domainToast, toast, t]);
}
